import React from 'react'
import toNumber from 'lodash/toNumber'
import { getWindow } from '@ztechapps/common.utils'
import { ColumnResizeHandle, useColumnResizeDrag } from '@ztechapps/webui.data-tools'
import { usePivotGridContext } from './Context'

export type PivotResizeHandleProps = { index: number; width: number }

/** The handle's own ancestor `th`'s CONTENT-box width — its border-box width minus its own
 * computed padding and border, the same quantity `PivotReportPage.ts`'s `contentBoxWidth()`
 * asserts against. Read live off the DOM at mousedown so the drag always starts from what the
 * column is actually showing, never from `measuredWidths` in `PivotGrid`, a
 * grow-only cache that can still hold a wider row's width after that row scrolled out of view. */
const readHeaderContentWidth = (event: React.MouseEvent): number | undefined => {
  const th = (event.currentTarget as HTMLElement).closest('th')
  if (!th) return undefined
  const style = getWindow().getComputedStyle(th)
  const toPixels = (value: string) => toNumber(value.replace('px', ''))
  return (
    th.getBoundingClientRect().width -
    toPixels(style.paddingLeft) -
    toPixels(style.paddingRight) -
    toPixels(style.borderLeftWidth) -
    toPixels(style.borderRightWidth)
  )
}

/** Drag handle at the right of a Rows-field or measure header cell — resizes its column. */
export const PivotResizeHandle = ({ index, width }: PivotResizeHandleProps) => {
  const { onColumnDrag, onColumnResize, onColumnReset } = usePivotGridContext()
  const { onStart } = useColumnResizeDrag({
    width,
    getWidth: (event) => readHeaderContentWidth(event) ?? width,
    onDrag: (next) => onColumnDrag(index, next),
    onResize: (next) => onColumnResize(index, next),
  })

  return (
    <ColumnResizeHandle
      onMouseDown={onStart}
      onDoubleClick={() => onColumnReset(index)}
      title="Drag to resize, double-click to reset"
      hoverable
    />
  )
}
